import {useFloating} from '@floating-ui/react';
import FocusTrap from 'focus-trap-react';
import React, {useRef, useState} from 'react';

//#region Year

interface YearProps {
    year: number
}

function Year(props: YearProps) {
    return <div className="month-year-picker__year">
            {props.year}
        </div>;
}

//#endregion Year

//#region Month

interface MonthProps {
    caption: string,
    selectedMonth: Date|undefined,
    date: Date,
    onClick?: (date: Date) => void
}

function Month(props: MonthProps) {
    return <div className={`month-year-picker__month ${(props.selectedMonth?.toDateString() === props.date.toDateString() ? 'month-year-picker__month--selected' : '')}`}
            onClick={() => props.onClick?.(props.date)}>
        {props.caption}
    </div>;
}

//#endregion Month

//#region MonthRow

interface MonthRowProps {
    months: string[],
    rowNumber: number,
    selectedMonth: Date|undefined,
    selectedYear: number,
    onClick?: (date: Date) => void
}

function MonthRow(props: MonthRowProps) {
    return <div className="month-year-picker__month__row">
        {props.months.map((month, index) =>
            <Month key={`${month}-${index}`} caption={month}
                selectedMonth={props.selectedMonth}
                date={new Date(props.selectedYear, props.rowNumber * props.months.length + index, 1)}
                onClick={props.onClick} />
        )}
    </div>;
}

//#endregion MonthRow
export default function MonthYearPicker(props: Props) {
    const date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const [selectedMonth, setSelectedMonth] = useState<Date>(props.value ?? date);
    const [selectedYear, setSelectedYear] = useState<number>(props.value?.getFullYear() ?? new Date().getFullYear());
    const [isPopperOpen, setIsPopperOpen] = useState(false);

    const {
            refs,
            floatingStyles
        } = useFloating({
            placement: 'bottom-start',
        });

    if(props.value && props.value !== selectedMonth) {
        setSelectedMonth(props.value);
        setSelectedYear(props.value.getFullYear());
    }

    const buttonRef = useRef<HTMLButtonElement>(null);

    const pickerLang = {
        months: [
            ['JAN', 'FEB', 'MAR'],
            ['APR', 'MAY', 'JUN'],
            ['JUL', 'AUG', 'SEP'],
            ['OCT', 'NOV', 'DEC']],
        monthsLong: [
            'JANUARY', 'FEBRUARY', 'MARCH',
            'APRIL','MAY', 'JUNE',
            'JULY', 'AUGUST', 'SEPTEMBER',
            'OCTOBER', 'NOVEMBER', 'DECEMBER']
    };

    const handleSelect = (date: Date) => {
        setSelectedMonth(date);
        setSelectedYear(date.getFullYear());
        closePopper();
        props.onChange?.(date.getMonth()+1, date.getFullYear());
    };

    const openPopper = () => {
        if(!props.isReadOnly) setIsPopperOpen(true);
    };

    const closePopper = () => {
        setIsPopperOpen(false);
    };

    return (
        <div className="month-year-picker">
            <button className={`month-year-picker__button ${props.isReadOnly ? 'month-year-picker__button--readonly' : ''}`} ref={buttonRef} onClick={openPopper}>
                {pickerLang.monthsLong[selectedMonth.getMonth()]} {selectedMonth.getFullYear()}<i className="fas fa-caret-down" />
            </button>
            {isPopperOpen && (
                <FocusTrap active focusTrapOptions={{
                    clickOutsideDeactivates: true,
                    onDeactivate: closePopper,
                    fallbackFocus: buttonRef.current as any
                }}>
                    <div
                        tabIndex={-1}
                        style={floatingStyles}
                        className="dialog-sheet"
                        ref={refs.setFloating}
                        role="dialog"
                    >
                        <div className="month-year-picker__selector">
                            <div className="month-year-picker__selector__previous" onClick={() => setSelectedYear(selectedYear-1 < props.minYear ? props.minYear : selectedYear-1)}>&lt;</div>
                            <Year year={selectedYear} />
                            <div className="month-year-picker__selector__next" onClick={() => setSelectedYear(selectedYear+1 > props.maxYear ? props.maxYear : selectedYear+1)}>&gt;</div>
                        </div>

                        <div className="month-year-picker__month__container">
                            {pickerLang.months.map((monthNames:string[], i:number) =>
                                <MonthRow key={`monthrow-${i}`}
                                    months={monthNames}
                                    rowNumber={i}
                                    selectedMonth={selectedMonth}
                                    selectedYear={selectedYear}
                                    onClick={handleSelect}/>
                            )}
                        </div>
                    </div>
                </FocusTrap>
            )}
        </div>
    );
}

type Props = {
    value?: Date | undefined,
    minYear: number,
    maxYear: number,
    isReadOnly?: boolean,
    onChange?: (month: number, year: number) => void
}
